/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Phase 3.6 — DJ engine ownership hook.
 *
 * Owns one DjEngine instance for the lifetime of the host's room session.
 * Publishes its master broadcast track to LiveKit. Also owns the
 * BroadcastChannel `stackroom-dj-board-${roomId}` that the optional pop-out
 * DJ board uses to remote-control the engine.
 *
 * Round 1 contract:
 *   - Engine ALWAYS lives in the main room tab. The pop-out window is a
 *     pure UI/remote-control client; closing it does NOT touch LiveKit.
 *   - Inline DJ console still uses the engine directly (no BC hop) so
 *     latency stays zero when the pop-out is closed.
 *   - When the pop-out window posts a heartbeat, `popoutAlive` flips true
 *     and the main tab can replace the inline console with a placeholder
 *     "DJ Board is in a separate window — [Bring back]" card.
 *
 * Message protocol (BroadcastChannel `stackroom-dj-board-${roomId}`):
 *   Pop-out → main:
 *     { type: "hello" }                              — popout window opened
 *     { type: "heartbeat" }                          — every ~1s
 *     { type: "goodbye" }                            — popout closing
 *     { type: "request-state" }                      — popout wants a fresh snapshot
 *     { type: "cmd", deck: "A"|"B", action, ...args }
 *     { type: "xfader", value: -1..1 }
 *     { type: "master", value: 0..1.2 }
 *     { type: "file_picked", deck: "A"|"B", file: File }
 *
 *   Main → pop-out:
 *     { type: "hello-ack" }                          — main is alive; full state will follow
 *     { type: "deck-state", deck: "A"|"B", state }   — title/playing/pos/dur/bpm/hotCues/loop/level
 *     { type: "mixer-state", xfader, master, vu }    — master VU + xfader + master gain
 *
 * Notes:
 *   - BroadcastChannel postMessage CAN carry File/Blob via structured clone.
 *     The pop-out's file input handler ships the picked File directly here;
 *     deck.loadFile() is called on the engine, which fires its existing
 *     "loaded" event so the inline console (when it eventually re-mounts) and
 *     the popout both stay in sync via the next state broadcast.
 */
import { useEffect, useRef, useState } from "react";
import { LocalAudioTrack, Track, ConnectionState, RoomEvent } from "livekit-client";
import { DjEngine } from "./DjEngine";

const HEARTBEAT_TIMEOUT_MS = 3000;
const STATE_BROADCAST_MS = 33; // ~30Hz while popout is alive

// Phase A R5 follow-up — persist Auto-DJ playlist metadata only (no File objects).
// Library-sourced rows are restored with file=null + sourceHandle so the UI can
// surface a Re-attach button. One-off uploads (no sourceHandle) cannot survive
// a refresh and are dropped on rehydrate.
const PLKEY = (rid) => `sr-autodj-playlist:${rid}`;

function loadSavedPlaylist(roomId) {
  try {
    const raw = localStorage.getItem(PLKEY(roomId));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    // Drop rows that can't be re-attached (no sourceHandle = one-off upload).
    return arr.filter((t) => t && t.sourceHandle).map((t) => ({
      ...t,
      file: null,            // File objects can't survive serialization
    }));
  } catch { return []; }
}

function savePlaylist(roomId, list) {
  try {
    const slim = (list || []).map(({ file, hasFile, ...rest }) => rest);
    localStorage.setItem(PLKEY(roomId), JSON.stringify(slim));
  } catch { /* */ }
}

export function useDjEngine({ room, roomId, enabled = true, onNowPlaying }) {
  const engineRef = useRef(null);
  const bcRef = useRef(null);
  const lkTrackRef = useRef(null);
  const publishedRef = useRef(false);
  const lastHeartbeatRef = useRef(0);
  const aliveRef = useRef(false);
  const [popoutAlive, setPopoutAlive] = useState(false);
  // Sticky — flips true on first hello and stays true so the main-tab
  // panel can render `dj-reopen-board` after the popout is closed.
  const [popoutEverOpened, setPopoutEverOpened] = useState(false);

  useEffect(() => { aliveRef.current = popoutAlive; }, [popoutAlive]);

  useEffect(() => {
    if (!enabled || !roomId) return;

    const eng = new DjEngine();
    engineRef.current = eng;

    // Phase A R5 follow-up — Rehydrate Auto-DJ playlist from localStorage.
    // Library-sourced rows are restored with file=null + sourceHandle so the UI
    // can render Re-attach buttons. One-off uploads are dropped on save (no
    // sourceHandle) — they can't survive a refresh, which is a real browser
    // limitation we surface gracefully via the UX.
    const restored = loadSavedPlaylist(roomId);
    if (restored.length) {
      eng.autoDj.playlist = restored;
      eng._emitAutoDj();
    }
    // Persist on every Auto-DJ mutation. Metadata only.
    const offSave = eng.onAutoDj((s) => savePlaylist(roomId, s.playlist));

    const bc = new BroadcastChannel(`stackroom-dj-board-${roomId}`);
    bcRef.current = bc;

    const sendDeckState = (D) => {
      const deck = D === "A" ? eng.deckA : D === "B" ? eng.deckB : D === "C" ? eng.deckC : eng.deckD;
      if (!deck) return;
      try {
        bc.postMessage({
          type: "deck-state",
          deck: D,
          state: {
            title: deck.title || "",
            playing: deck.playing,
            pos: deck.currentTime || 0,
            dur: deck.duration || 0,
            bpm: deck.bpm || 0,
            pitch: deck.pitch || 0,
            firstBeatTime: deck.firstBeatTime || 0,
            slipActive: !!deck.slipActive,
            killed: {
              low:  deck.isEqKilled?.("low")  || false,
              mid:  deck.isEqKilled?.("mid")  || false,
              high: deck.isEqKilled?.("high") || false,
            },
            hotCues: [...(deck.hotCues || [null, null, null, null, null, null, null, null])],
            loop: {
              start: deck.loop?.start ?? null,
              end: deck.loop?.end ?? null,
              on: !!deck.loop?.enabled,
            },
            level: deck.getLevel(),
            // Phase A R6 B2 — flag that lets the pop-out's MirroredWaveform
            // know when band data has been received. The actual bands Float32Array
            // ships separately via the "deck-bands" BC message below.
            hasBands: !!deck._bands,
          },
        });
      } catch (err) { /* channel may be closed */ }
    };

    // Phase A R6 B2 — broadcast computed bands (Float32Array) to the pop-out
    // exactly once per track-load, when the engine's worker resolves. The
    // pop-out's RemoteDeck caches them so MirroredWaveform can draw without
    // re-running a worker (it doesn't have access to PCM in the pop-out).
    const sendDeckBands = (D) => {
      const deck = D === "A" ? eng.deckA : D === "B" ? eng.deckB : D === "C" ? eng.deckC : eng.deckD;
      if (!deck?._bands) return;
      try {
        bc.postMessage({
          type: "deck-bands",
          deck: D,
          bands: deck._bands,
          columns: deck._bandsColumns || 0,
        });
      } catch (err) { /* */ }
    };

    const sendMixerState = () => {
      try {
        bc.postMessage({
          type: "mixer-state",
          xfader: eng.crossfader,
          master: eng.master.gain.value,
          vu: eng.readVU(),
        });
      } catch (err) { /* */ }
    };

    const sendFxState = () => {
      if (!eng.fxRack?.getState) return;
      try {
        bc.postMessage({ type: "fx-state", state: eng.fxRack.getState() });
      } catch (err) { /* */ }
    };

    const sendAutoDjState = () => {
      const a = eng.autoDj; if (!a) return;
      // Strip File objects (not transferable across BroadcastChannel cleanly
      // for all browsers) — popout displays metadata + a flag so it knows
      // which rows are playable.
      const playlist = a.playlist.map(({ file, ...rest }) => ({
        ...rest,
        hasFile: !!file,
      }));
      try {
        bc.postMessage({
          type: "autodj-state",
          state: { ...a, playlist },
        });
      } catch (err) { /* */ }
    };

    const sendFullState = () => {
      sendDeckState("A");
      sendDeckState("B");
      sendDeckState("C");
      sendDeckState("D");
      // Phase A R6 B2 — also send cached bands so a freshly-opened pop-out
      // doesn't have to wait for the next track-load to render its waveform.
      sendDeckBands("A");
      sendDeckBands("B");
      sendDeckBands("C");
      sendDeckBands("D");
      sendMixerState();
      sendFxState();
      sendAutoDjState();
    };

    // Phase A R6 B2 — when the engine's waveform worker resolves for a deck,
    // ship the bands once over BC. The pop-out caches them on its RemoteDeck.
    eng.deckA?.on?.("bands-ready", () => sendDeckBands("A"));
    eng.deckB?.on?.("bands-ready", () => sendDeckBands("B"));
    eng.deckC?.on?.("bands-ready", () => sendDeckBands("C"));
    eng.deckD?.on?.("bands-ready", () => sendDeckBands("D"));

    bc.onmessage = async (e) => {
      const m = e.data;
      if (!m || typeof m !== "object") return;

      switch (m.type) {
        case "hello":
          lastHeartbeatRef.current = Date.now();
          setPopoutAlive(true);
          setPopoutEverOpened(true);
          try { bc.postMessage({ type: "hello-ack" }); } catch { /* */ }
          sendFullState();
          return;
        case "heartbeat":
          lastHeartbeatRef.current = Date.now();
          setPopoutAlive(true);
          return;
        case "goodbye":
          setPopoutAlive(false);
          return;
        case "request-state":
          sendFullState();
          return;
        case "xfader":
          eng.setCrossfader(m.value);
          sendMixerState();
          return;
        case "master":
          eng.setMasterGain(m.value);
          sendMixerState();
          return;
        case "cmd": {
          // Phase A R11 A/B — engine.transition() proxy from the popout.
          // Echo + Loop styles need engine-level orchestration (FX wet
          // bus + true loop locking), so the popout posts a single
          // command and we run the whole transition here, then ack.
          if (m.target === "engine" && m.action === "transition") {
            (async () => {
              // eslint-disable-next-line no-console
              console.log(`[engine] cmd:engine:transition style=${m.style} from=${m.from} to=${m.to} dur=${m.dur} reqId=${m.reqId}`);
              try {
                if (eng.ctx?.state === "suspended") {
                  try { await eng.resume(); } catch (err) { /* eslint-disable-next-line no-console */ console.warn("[engine] transition: resume failed:", err); }
                }
                await eng.transition(m.from, m.to, m.style, m.dur);
                try {
                  bc.postMessage({ type: "transition-done", reqId: m.reqId, ok: true });
                } catch (err) { /* eslint-disable-next-line no-console */ console.error("[engine] transition ack post failed:", err); }
              } catch (err) {
                // eslint-disable-next-line no-console
                console.error("[engine] transition handler failed:", err);
                try {
                  bc.postMessage({ type: "transition-done", reqId: m.reqId, ok: false, error: String(err?.message || err) });
                } catch (e2) { /* eslint-disable-next-line no-console */ console.error("[engine] transition ack post failed:", e2); }
              }
            })();
            return;
          }
          // Phase A R5 follow-up — Auto-DJ commands are also routed via
          // {type:"cmd", target:"autodj", action:...}. Route those FIRST
          // so the deck-action switch below doesn't swallow them.
          if (m.target === "autodj") {
            if (m.action === "add") {
              // Phase A R8.1 — track may carry an ArrayBuffer + fileName +
              // mime instead of a File. Reconstruct the File on this side
              // so the engine's deck loaders + waveform/BPM workers don't
              // need to know about the transport format.
              let track = m.track || {};
              if (track.arrayBuffer && track.fileName) {
                try {
                  const file = new File([track.arrayBuffer], track.fileName, { type: track.mime || "" });
                  // eslint-disable-next-line no-console
                  console.log(`[engine] received cmd:autodj:add, reconstructing File from ${track.arrayBuffer.byteLength} bytes (name=${track.fileName})`);
                  const { arrayBuffer, fileName, mime, ...rest } = track;
                  track = { ...rest, file };
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.error("[engine] failed to reconstruct File:", err);
                }
              }
              eng.addToPlaylist?.(track);
              // eslint-disable-next-line no-console
              console.log(`[engine] playlist now has ${eng.autoDj?.playlist?.length || 0} entries`);
            }
            if (m.action === "remove")  eng.removeFromPlaylist?.(m.index);
            if (m.action === "reorder") eng.reorderPlaylist?.(m.from, m.to);
            if (m.action === "attach") {
              // Phase A R8.1 — same ArrayBuffer path for Re-attach.
              let file = null;
              if (m.arrayBuffer && m.fileName) {
                try {
                  file = new File([m.arrayBuffer], m.fileName, { type: m.mime || "" });
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.error("[engine] attach: File reconstruct failed:", err);
                }
              } else if (m.file instanceof File || m.file instanceof Blob) {
                file = m.file;
              }
              if (file) eng.attachPlaylistFile?.(m.trackId, file);
            }
            if (m.action === "load-deck") {
              // Phase A R8.2 — popout asks main tab to load a playlist
              // track onto deck A or B. The main tab is the canonical
              // source for files (R8.1 reconstructs them here). We look
              // up the row by trackId in eng.autoDj.playlist and call
              // deck.loadFile on the actual DjEngine deck. Result is
              // posted back as {type:"autodj-loaded", reqId, ok, ...}.
              const D = m.deck === "B" ? "B" : "A";
              const deck = D === "A" ? eng.deckA : eng.deckB;
              const row = (eng.autoDj?.playlist || []).find((t) => t.trackId === m.trackId);
              // eslint-disable-next-line no-console
              console.log(`[engine] cmd:autodj:load-deck trackId=${m.trackId} deck=${D} foundRow=${!!row} hasFile=${!!row?.file} ctx.state=${eng.ctx?.state}`);
              if (!row || !row.file) {
                try {
                  bc.postMessage({
                    type: "autodj-loaded", reqId: m.reqId, ok: false,
                    error: row ? `no File on playlist row trackId=${m.trackId}` : `trackId=${m.trackId} not in playlist`,
                  });
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.error("[engine] load-deck ack post failed:", err);
                }
                sendAutoDjState();
                return;
              }
              try {
                // Resume audio context — main-tab ctx may have suspended
                // while the popout was active. resume() needs a recent
                // user gesture in main tab; the popout's open-window
                // click counted as one. If it has been longer, this may
                // fail and we surface that to the user.
                if (eng.ctx?.state === "suspended") {
                  // eslint-disable-next-line no-console
                  console.log(`[engine] ctx suspended; resuming before deck.loadFile`);
                  try { await eng.resume(); } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error("[engine] ctx.resume failed:", err);
                  }
                }
                // eslint-disable-next-line no-console
                console.log(`[engine] ctx.state=${eng.ctx?.state}, calling deck.loadFile name=${row.file.name} size=${row.file.size}`);
                await deck.loadFile(row.file);
                // eslint-disable-next-line no-console
                console.log(`[engine] deck.loadFile resolved deck=${D} duration=${deck.duration} audioEl=${!!deck.audioEl}`);
                sendDeckState(D);
                try { onNowPlaying?.(D, deck.title || row.file.name); } catch { /* */ }
                try {
                  bc.postMessage({
                    type: "autodj-loaded", reqId: m.reqId, ok: true,
                    duration: deck.duration || 0,
                    bpm: deck.bpm || 0,
                    key: deck.detectedKey || null,
                    title: deck.title || "",
                  });
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.error("[engine] load-deck ack post failed:", err);
                }
              } catch (err) {
                // eslint-disable-next-line no-console
                console.error("[engine] cmd:autodj:load-deck failed:", err);
                try {
                  bc.postMessage({
                    type: "autodj-loaded", reqId: m.reqId, ok: false,
                    error: String(err?.message || err),
                  });
                } catch (e2) {
                  // eslint-disable-next-line no-console
                  console.error("[engine] load-deck ack post failed:", e2);
                }
              }
              return;
            }
            if (m.action === "set")     eng.setAutoDj?.(m.patch);
            // The autodj-state broadcast happens automatically on the next
            // periodic tick; trigger an immediate one so popout sees the
            // mutation without waiting for the next 33ms frame.
            sendAutoDjState();
            return;
          }
          const D = m.deck === "B" ? "B" : m.deck === "C" ? "C" : m.deck === "D" ? "D" : "A";
          const deck = eng._deck ? eng._deck(D) : (D === "B" ? eng.deckB : eng.deckA);
          if (!deck) return;
          switch (m.action) {
            case "play":         deck.play(); break;
            case "pause":        deck.pause(); break;
            case "cue":          deck.cue(); break;
            case "set_high":     deck.setHigh(m.value); break;
            case "set_mid":      deck.setMid(m.value); break;
            case "set_lo":       deck.setLow(m.value); break;
            case "set_filter":   deck.setFilter(m.value); break;
            case "set_gain":     deck.setGain(m.value); break;
            case "set_channel":  deck.setChannel(m.value); break;
            case "set_pitch":    deck.setPitch(m.value); break;
            case "seek_to":      deck.seekTo(m.value); break;
            case "sync": {
              // Phase A R10 B — sync to caller-provided LIVE BPM
              // (master deck's effectiveBpm). The popout's RemoteDeck
              // computes the live value of the master and posts it.
              // If no value is provided, fall back to "other deck's
              // effectiveBpm" so legacy callers still work.
              const otherDeck = D === "B" ? eng.deckA : eng.deckB;
              const target = (m.targetBpm && m.targetBpm > 0) ? m.targetBpm : (otherDeck?.effectiveBpm || otherDeck?.bpm || 0);
              // eslint-disable-next-line no-console
              console.log(`[sync] target=${D} masterLiveBPM=${target?.toFixed(2)} targetDetectedBPM=${deck?.bpm?.toFixed(2)} (current pitch=${deck?.pitch?.toFixed(2)}%)`);
              deck.sync(target);
              // eslint-disable-next-line no-console
              console.log(`[sync] result: deck ${D} new pitch=${deck?.pitch?.toFixed(2)}% effective=${deck?.effectiveBpm?.toFixed(2)} BPM`);
              break;
            }
            case "hot_cue_set":   deck.setHotCue(m.slot); break;
            case "hot_cue_jump":  deck.jumpHotCue(m.slot); break;
            case "hot_cue_clear": deck.clearHotCue(m.slot); break;
            case "loop_in":
              deck.loop.start = deck.currentTime;
              break;
            case "loop_out":
              if (deck.loop.start != null && deck.currentTime > deck.loop.start) {
                deck.loop.end = deck.currentTime;
              }
              break;
            case "loop_toggle":       deck.toggleLoop(); break;
            // Phase A R2 — loop rolls, slip, EQ kill, tap-tempo, xfader assign
            case "loop_roll_start":   deck.startLoop(m.beats, !!m.momentary); break;
            case "loop_roll_release": deck.releaseLoop(); break;
            case "slip_toggle":       deck.setSlip(!deck.slipActive); break;
            case "eq_kill":           deck.killEq(m.band); break;
            case "tap_tempo":         deck.tapTempo(); break;
            case "tap_downbeat":      deck.setDownbeatAnchor(); break;
            case "set_xfader_assign": eng.setDeckXfaderAssign?.(D, m.assign); break;
            default: break;
          }
          sendDeckState(D);
          return;
        }
        case "fx": {
          if (!eng.fxRack) return;
          if (m.action === "enable")   eng.fxRack.setEnabled(m.name, !!m.value);
          if (m.action === "param")    eng.fxRack.setParam(m.name, m.param, m.value);
          if (m.action === "send")     eng.fxRack.setSendFrom(m.name, m.source);
          sendFxState();
          return;
        }
        case "mix_now": {
          // Phase A R5 F1 — popout asked for a manual crossfade.
          try { eng.mixNow?.(m.durationSec); } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[useDjEngine] mix_now failed:", err);
          }
          return;
        }
        case "file_picked": {
          if (!(m.file instanceof File)) return;
          const D = m.deck === "B" ? "B" : m.deck === "C" ? "C" : m.deck === "D" ? "D" : "A";
          const deck = eng._deck ? eng._deck(D) : (D === "B" ? eng.deckB : eng.deckA);
          if (!deck) return;
          try {
            await deck.loadFile(m.file);
            sendDeckState(D);
            // Bubble "now playing" up to the room header / metadata layer.
            try { onNowPlaying?.(D, deck.title || m.file.name); } catch { /* */ }
            // Detect BPM in background and re-broadcast when ready.
            deck.detectBPM().then(() => sendDeckState(D)).catch(() => {});
          } catch (err) {
            console.warn("[useDjEngine] file_picked load failed", err);
          }
          return;
        }
        default: return;
      }
    };

    // Periodic state broadcast (only while popout is alive — saves cycles when no one is listening).
    let rafId;
    let lastBroadcast = 0;
    const tick = (now) => {
      if (aliveRef.current && (now - lastBroadcast) >= STATE_BROADCAST_MS) {
        lastBroadcast = now;
        sendFullState();
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    // Heartbeat timeout watcher: if popout went silent >3s, flip to "not alive".
    const hbInterval = setInterval(() => {
      if (aliveRef.current && Date.now() - lastHeartbeatRef.current > HEARTBEAT_TIMEOUT_MS) {
        setPopoutAlive(false);
      }
    }, 1000);

    // Phase A R5 follow-up — Auto-DJ file transfer from pop-out.
    // BroadcastChannel structured-clones Files in modern browsers, but
    // window.postMessage from window.opener is more reliable cross-window
    // and is the channel the pop-out uses for binary track payloads.
    // Expected payload: { type: "stackroom-autodj-file", roomId, trackId, file }.
    const onWindowMessage = (e) => {
      const d = e.data; if (!d || typeof d !== "object") return;
      if (d.type !== "stackroom-autodj-file") return;
      if (d.roomId && d.roomId !== roomId) return;
      try {
        if (d.file instanceof File || d.file instanceof Blob) {
          eng.attachPlaylistFile?.(d.trackId, d.file);
        }
      } catch (err) { /* */ }
    };
    window.addEventListener("message", onWindowMessage);

    // LiveKit publish — broadcast track stays in the main tab regardless of UI location.
    const setupPublish = async () => {
      try {
        await eng.resume();
        // eslint-disable-next-line no-console
        console.log(`[useDjEngine] ctx.resume on init; state=${eng.ctx?.state}`);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`[useDjEngine] ctx.resume on init failed (will retry on user action):`, err);
      }
      if (!room) return;
      const tryPublish = async () => {
        if (publishedRef.current) return;
        try {
          const mst = eng.getBroadcastTrack();
          if (!mst) return;
          const lkTrack = new LocalAudioTrack(mst);
          await room.localParticipant.publishTrack(lkTrack, {
            name: "DJ Console Master",
            source: Track.Source.Microphone,
          });
          publishedRef.current = true;
          lkTrackRef.current = lkTrack;
        } catch (err) {
          console.warn("[useDjEngine] DJ publish failed:", err);
        }
      };
      if (room.state === ConnectionState.Connected) await tryPublish();
      else room.once(RoomEvent.Connected, tryPublish);
    };
    setupPublish();

    return () => {
      cancelAnimationFrame(rafId);
      clearInterval(hbInterval);
      try { window.removeEventListener("message", onWindowMessage); } catch { /* */ }
      try { offSave?.(); } catch { /* */ }
      try { bc.close(); } catch { /* */ }
      try { eng.dispose(); } catch { /* */ }
      engineRef.current = null;
      bcRef.current = null;
      publishedRef.current = false;
      lkTrackRef.current = null;
    };
  }, [enabled, roomId, room]);

  const openPopout = () => {
    if (!roomId) return null;
    return window.open(
      `/r/${roomId}/dj-board`,
      `stackroom-dj-board-${roomId}`,
      "width=1280,height=820,resizable=yes,toolbar=no,menubar=no,location=no"
    );
  };

  return { engineRef, popoutAlive, popoutEverOpened, openPopout };
}
